import { useMatches } from '@tanstack/react-router'
import clsx from 'clsx'
import React, { useMemo } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { CompanySideNav } from '#components/CompanySideNav'
import { Icon } from '#components/Icon'
import { UserSideNav } from '#components/UserSideNav'
import { useSidebar } from '#hooks/useSidebar'
import { useFetchCompanyName } from '#pages/CompanyDetail/hooks/useFetchCompanyName'
import { useFetchUserName } from '#pages/UserDashboard/hooks/useFetchUserName'
import { CompanyContractProvider } from '#providers/CompanyContractProvider'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

export { ExpandableNavItem } from './components/ExpandableNavItem'
export { NavigationItem } from './components/NavigationItem'
export { NavigationSection } from './components/NavigationSection'

const COMPANY_ROUTE_PATTERN = /^\/companies\/([^/]+)/
const USER_ROUTE_PATTERN = /^\/companies\/([^/]+)\/users\/([^/]+)/

export const SideNav = ({ children }: IProps): React.JSX.Element => {
  const { t } = useTranslation()
  const { isCollapsed, isMobile, toggleSidebar } = useSidebar()
  const matches = useMatches()

  const { companyId, userId } = useMemo((): { companyId: string | null; userId: string | null } => {
    const lastMatch = matches[matches.length - 1]
    if (!lastMatch) return { companyId: null, userId: null }

    const userMatch = USER_ROUTE_PATTERN.exec(lastMatch.pathname)
    if (userMatch) return { companyId: userMatch[1], userId: userMatch[2] }

    const companyMatch = COMPANY_ROUTE_PATTERN.exec(lastMatch.pathname)
    if (companyMatch) return { companyId: companyMatch[1], userId: null }

    return { companyId: null, userId: null }
  }, [matches])

  const { companyName } = useFetchCompanyName(companyId ?? '')
  const { userName } = useFetchUserName(companyId ?? '', userId ?? '')
  const isCompanyView = companyId !== null
  const isUserView = userId !== null

  return (
    <div className={clsx(styles.navigation, isCollapsed && styles.collapsed)} data-testid="side-nav">
      <div className={clsx(styles.panels, isCompanyView && styles.panelsCompany)}>
        {/* The panel out of view is inert, so keyboard and screen readers skip it */}
        <nav className={styles.panel} aria-label={t('Main navigation')} inert={isCompanyView}>
          {children}
        </nav>
        <nav
          className={styles.panel}
          aria-label={isUserView ? t('User navigation') : t('Company navigation')}
          inert={!isCompanyView}
        >
          {isUserView && companyId && userId ? (
            <UserSideNav userId={userId} userName={userName} companyId={companyId} companyName={companyName} />
          ) : (
            companyId && (
              <CompanyContractProvider companyId={companyId}>
                <CompanySideNav companyId={companyId} companyName={companyName} />
              </CompanyContractProvider>
            )
          )}
        </nav>
      </div>

      {!isMobile && (
        <div className={styles.toggleArea}>
          <Button
            className={styles.toggleButton}
            onPress={toggleSidebar}
            aria-label={isCollapsed ? t('Expand sidebar') : t('Collapse sidebar')}
            data-testid="sidebar-toggle"
          >
            <Icon
              name="chevron-left"
              width="16"
              height="16"
              className={clsx(styles.toggleIcon, isCollapsed && styles.toggleIconRotated)}
            />
          </Button>
        </div>
      )}
    </div>
  )
}
